import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { getUpcomingLoanPayments as getUpcomingLoanPaymentsThunk } from 'thunks';
import { getUpcomingLoanPayments } from 'selectors/getUpcomingLoanPayments';
import { getApplicationData } from 'selectors/getApplicationData';
import { ApplicationStatusName } from 'enums/ApplicationStatusName';
import { useNavigate } from 'hooks/useNavigate';
import { RoutePath } from 'enums/Routes';
import { PaymentMethod } from 'enums/PaymentMethod';

import { IconName, VisualType, UpcomingItem, UpcomingItemVisual } from './useUpcomingData';
import { formatUpcomingDate } from './upcomingSectionUtils';

const STATUSES_TO_SHOW_PAYMENTS = [ApplicationStatusName.AwaitingFunding, ApplicationStatusName.Funded];

const getPaymentMethodLabel = (paymentMethod?: PaymentMethod): string => {
  switch (paymentMethod) {
    case PaymentMethod.ACH:
      return 'ACH';
    case PaymentMethod.Payroll:
    default:
      return 'Direct deposit from paycheck';
  }
};

export const useUpcomingLoanPayments = (customApplicationId?: string): UpcomingItem[] => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { application } = useSelector(getApplicationData);
  const { payments, applicationId: loadedApplicationId } = useSelector(getUpcomingLoanPayments);
  const { status: applicationStatus, id: defaultApplicationId, paymentMethod } = application ?? {};

  const applicationId = customApplicationId || defaultApplicationId;

  useEffect(() => {
    if (
      applicationId &&
      applicationStatus &&
      STATUSES_TO_SHOW_PAYMENTS.includes(applicationStatus) &&
      loadedApplicationId !== applicationId
    ) {
      dispatch(getUpcomingLoanPaymentsThunk(applicationId));
    }
  }, [applicationId, applicationStatus, loadedApplicationId, dispatch]);

  return payments
    .map((payment) => {
      const { id, chargeAmount: value, date } = payment;
      const groupLabel = formatUpcomingDate(date);
      const visual: UpcomingItemVisual = {
        name: IconName.Transfer,
        type: VisualType.Icon,
      };

      if (!groupLabel) return null;

      return {
        id,
        value,
        visual,
        groupLabel,
        title: 'Automatic Loan Payment',
        description: getPaymentMethodLabel(paymentMethod),
        date: date ? new Date(date) : undefined,
        onClick: () => navigate(RoutePath.LoanPayment.replace(':id', id) as RoutePath),
      };
    })
    .filter((item) => item !== null) as UpcomingItem[];
};
